import React, {useState, useEffect, useCallback } from 'react';
import {useNavigate } from 'react-router-dom';
import {getAuth, User, signOut } from 'firebase/auth';

import {
  AdminSidebar,
  AdminLayout,
  MainContent,
  UserInfo,
  LogoutButton,
  SidebarList,
  SidebarItem,
  SidebarHeader,
  InactivityWarning,
  CountdownTimer,
  Button,
} from './AdminDashboard.styles';

import EstateManagement from './RealEstateManagement';
import StallManagement from './StallManagement';
import UserManagement from './UserManagement';
import AdministrativeUnits from './AdministrativeUnits';
import SaleSubmissionsAdmin from './SaleSubmissionsAdmin';
import PurchaseRequestsAdmin from './PurchaseRequestsAdmin';
import AdminRegistration from './AdminRegistration';
import VerificationManagement from './documentVerification';
import SuppliersManagement from './SupplierManagement';
import StallConfigurationManagement from './partials/prices_categories_management';
import { PaymentsDashboard } from './payments/PaymentsDashboard';
import { TestimonialApprovalDashboard } from './Feedback';
import JobAdvertsManagement from './JobAdvertsManagement';

interface AdminDashboardProps {
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({onLogout }) => {
  const [activeSection, setActiveSection] = useState<
    'userManagement' | 
    'estateManagement' | 
    'stallManagement' | 
    'administrativeUnits' |
    'saleSubmissions' |
    'purchaseRequests' |
    'adminRegistration' |
    'verificationManagement' |
    'supplierManagement'|
    'stallConfiguration' |
    'paymentsManagement'|
    'testimonies' |
    'jobAdvertsManagement'
  >('userManagement');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60); // 60 seconds countdown
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);
  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(null);
  
  const auth = getAuth();
  const navigate = useNavigate();
  const isAdmin = true;

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear existing timers
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (countdownTimer) clearInterval(countdownTimer);
    
    setShowWarning(false);
    setCountdown(60);

    // Set new inactivity timer (4 minutes before warning, total 5 minutes)
    const timer = setTimeout(() => {
      setShowWarning(true);
      
      // Start countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            handleAutoLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setCountdownTimer(countdownInterval);
    }, 240000); // 4 minutes (240,000 ms)

    setInactivityTimer(timer);
  }, [inactivityTimer, countdownTimer]);

  // Handle auto logout
  const handleAutoLogout = useCallback(async () => {
    try {
      await signOut(auth);
      onLogout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Error during auto logout:', error);
    }
  }, [auth, onLogout, navigate]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  // Initialize timer on component mount
  useEffect(() => {
    resetInactivityTimer();
    
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [resetInactivityTimer, inactivityTimer, countdownTimer]);

  useEffect(() => {
    const user = auth.currentUser;
    setCurrentUser(user);

    if (!isAdmin) {
      navigate('/');
    }
  }, [isAdmin, navigate, auth]);

  if (!isAdmin) return null;

  return (
    <AdminLayout>
      {/* Inactivity Warning Modal */}
      {showWarning && (
        <InactivityWarning>
          <div style={{
            backgroundColor: 'white', 
            padding: '2rem', 
            borderRadius: '8px', 
            textAlign: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: '300px'
          }}>
            <h3 style={{color: '#e74c3c', marginBottom: '1rem' }}>Session Timeout Warning</h3>
            <p>You will be logged out due to inactivity in</p>
            <CountdownTimer>{countdown} seconds</CountdownTimer>
            <p>Move your mouse or press any key to continue your session.</p>
            <Button 
              onClick={resetInactivityTimer}
              style={{marginTop: '1rem' }}
            >
              Continue Session
            </Button>
          </div>
        </InactivityWarning>
      )}

      <AdminSidebar>
        <SidebarHeader>
          <h2>Admin Dashboard</h2>
        </SidebarHeader>

        {currentUser && (
          <UserInfo>
            <div style={{fontWeight: 'bold', marginBottom: '4px' }}>
              {currentUser.displayName || currentUser.email || 'Admin User'}
            </div>
            <div style={{fontSize: '0.8rem', color: '#666' }}>
              {currentUser.email}
            </div>
          </UserInfo>
        )}

        <SidebarList>
          <SidebarItem
            className={activeSection === 'userManagement' ? 'active' : ''}
            onClick={() => setActiveSection('userManagement')}
          >
            User Management
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'stallManagement' ? 'active' : ''}
            onClick={() => setActiveSection('stallManagement')}
          >
            Stall Management
          </SidebarItem>
          <SidebarItem
              className={activeSection === 'stallConfiguration' ? 'active' : ''}
              onClick={() => setActiveSection('stallConfiguration')}
            >
            Stall Config & Prices ⚙️
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'estateManagement' ? 'active' : ''}
            onClick={() => setActiveSection('estateManagement')}
          >            
            Real Estate Management
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'administrativeUnits' ? 'active' : ''}
            onClick={() => setActiveSection('administrativeUnits')}
          >
            Administrative Units
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'saleSubmissions' ? 'active' : ''}
            onClick={() => setActiveSection('saleSubmissions')}
          >
            Sale Submissions
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'purchaseRequests' ? 'active' : ''}
            onClick={() => setActiveSection('purchaseRequests')}
          >
            Purchase Requests
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'adminRegistration' ? 'active' : ''}
            onClick={() => setActiveSection('adminRegistration')}
          >
            Staff Management
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'verificationManagement' ? 'active' : ''}
            onClick={() => setActiveSection('verificationManagement')}
          >
             Document Verification
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'supplierManagement' ? 'active' : ''}
            onClick={() => setActiveSection('supplierManagement')}
          >
             Supplier Management
          </SidebarItem>
          <SidebarItem
            className={activeSection === 'testimonies' ? 'active' : ''}
            onClick={() => setActiveSection('testimonies')}
          >
             Testimonies
          </SidebarItem>

          <SidebarItem
            className={activeSection === 'jobAdvertsManagement' ? 'active' : ''}
            onClick={() => setActiveSection('jobAdvertsManagement')}
          >
             Job Adverts
          </SidebarItem>

            <SidebarItem
            className={activeSection === 'paymentsManagement' ? 'active' : ''}
            onClick={() => setActiveSection('paymentsManagement')}
          >
             Payment Info
          </SidebarItem>

          <SidebarItem>Content Moderation</SidebarItem>
          <SidebarItem>System Settings</SidebarItem>
          <SidebarItem>Reports</SidebarItem>
        </SidebarList>

        <LogoutButton onClick={onLogout}>
          Logout
        </LogoutButton>
      </AdminSidebar>

      <MainContent>
        {activeSection === 'userManagement' && <UserManagement />}
        {activeSection === 'stallManagement' && <StallManagement />}
        {activeSection === 'estateManagement' && <EstateManagement />}
        {activeSection === 'administrativeUnits' && <AdministrativeUnits />}
        {activeSection === 'saleSubmissions' && <SaleSubmissionsAdmin />}
        {activeSection === 'purchaseRequests' && <PurchaseRequestsAdmin />}
        {activeSection === 'adminRegistration' && <AdminRegistration />}
        {activeSection === 'verificationManagement' && <VerificationManagement />}
        {activeSection === 'supplierManagement' && <SuppliersManagement />}
        {activeSection === 'stallConfiguration' && <StallConfigurationManagement />}
        {activeSection === 'paymentsManagement' && <PaymentsDashboard/>}
        {activeSection === 'testimonies' && <TestimonialApprovalDashboard/>}
        {activeSection === 'jobAdvertsManagement' && <JobAdvertsManagement/>}

      </MainContent>
    </AdminLayout>
  );
};

export default AdminDashboard;